import { injectOrBuildContainer } from '@monofe/di';
import { memo, useCallback, useEffect, useState } from 'react';
import type {
    ITourItemViewModel,
    ITourMainProps as ITourMainProperties,
    ITourSideMenuItemViewProps as ITourSideMenuItemViewProperties,
    ITourSideMenuViewProps as ITourSideMenuViewProperties,
    ITourSubItemViewModel,
    ITourTopBarProps as ITourTopBarProperties,
} from '../interface';
import { useExpandedItemIndex } from '../util';
import MainView from './MainView';
import type {
    IHamburgerItemViewProperties,
    IHamburgerNavigationBarProperties,
    IHamburgerSideMenuDownloadViewProperties,
    IHamburgerSideMenuItemViewProperties,
    IHamburgerSideMenuViewProperties,
    IHamburgerSubItemViewProperties,
    ITextProperties,
} from './interface';

const MAX_ITEMS_VISIBLE_ONSCREEN = 4;
const DEFAULT_TEXT_PROPS_SIDEMENU: ITextProperties = Object.freeze({
    text: '',
    fontFamily: 'initial',
    fontSize: 'initial',
    fontAlignment: 'center',
    fontWeight: 500,
    lineHeight: 'initial',
    color: 'initial',
    visible: true,
});

function getOnClick(model: ITourSubItemViewModel) {
    const onClick = model.onClick;

    if (typeof onClick === 'string') {
        return onClick;
    } else if (onClick instanceof Function) {
        return () => {
            onClick(model);
        };
    } else {
        throw new TypeError(`unexpected type ${JSON.stringify(onClick)}`);
    }
}

function mapSubItem(model: ITourSubItemViewModel): IHamburgerSubItemViewProperties {
    return {
        ...model,
        foregroundImage: model.backgroundImage,
        onClick: getOnClick(model),
    };
}

interface IHamburgerItemViewData {
    index: number;
    model: ITourItemViewModel;
}

function mapItemToProperties(
    model: ITourItemViewModel,
    index: number,
    onItemClick: (item: IHamburgerItemViewProperties) => void,
): IHamburgerItemViewProperties {
    const data: IHamburgerItemViewData = {
        index,
        model,
    };

    return {
        ...model,
        subItems: model.subItems.map(x => mapSubItem(x)),
        isShowingSubItems: false,
        link: model.subItems.length === 0 ? model.link : undefined,
        data,

        onShow: function (this: IHamburgerItemViewProperties) {
            model.onShow?.(model);
        },
        onClick:
            typeof model.onClick === 'string'
                ? model.onClick
                : function (this: IHamburgerItemViewProperties) {
                      onItemClick(this);
                  },
        shouldLazyLoad: index > MAX_ITEMS_VISIBLE_ONSCREEN,
    };
}

function getOnClickSideMenu(model: ITourSideMenuItemViewProperties) {
    const onClick = model.onClick;

    if (typeof onClick === 'string') {
        return onClick;
    } else if (onClick instanceof Function) {
        return () => {
            onClick(model);
        };
    } else {
        throw new TypeError(`unexpected type ${JSON.stringify(onClick)}`);
    }
}

function mapSideMenuItem(
    model: ITourSideMenuItemViewProperties,
): IHamburgerSideMenuItemViewProperties {
    return {
        title: {
            ...model.title,
            fontAlignment: 'left',
        },
        backgroundColor: model.backgroundColor,

        onClick: getOnClickSideMenu(model),
    };
}

function mapSideMenuDownload(
    model: ITourSideMenuViewProperties,
    hasContentDownloaded: boolean,
    onDownloadToggle?: (newStatus: boolean) => void,
): IHamburgerSideMenuDownloadViewProperties {
    return {
        title: {
            ...DEFAULT_TEXT_PROPS_SIDEMENU,
            ...model.items.at(0)?.title,
            fontAlignment: 'left',
            text: 'Download',
        },
        backgroundColor: 'transparent',
        isDownloaded: hasContentDownloaded,
        onChange: onDownloadToggle,
    };
}

function mapTopBar(
    model: ITourTopBarProperties,
    showFloorPlanIcon: boolean,
    onHamburgerMenuClick: () => void,
    onFloorPlanClick?: () => void,
    floorplanHref?: string,
): IHamburgerNavigationBarProperties {
    return {
        ...model,
        showHamburgerMenuIcon: true,
        hamburgerMenuIconColor: model.iconColor,
        onHamburgerMenuClick,
        showFloorPlanIcon,
        floorPlanIconColor: model.iconColor,
        onFloorPlanClick,
        floorplanHref,
    };
}

function _changeIsShowing(
    inProperties: IHamburgerItemViewProperties,
    newIsShowing: boolean,
): IHamburgerItemViewProperties {
    if (inProperties.isShowingSubItems === newIsShowing) {
        return inProperties;
    }
    const outProperties = { ...inProperties };
    outProperties.isShowingSubItems = newIsShowing;
    return outProperties;
}

function View(properties: ITourMainProperties): JSX.Element | null {
    const {
        backgroundColor,
        backgroundImage,
        collection,
        sideMenu,
        topBar,
        showFloorPlanIcon,
        hasContentDownloaded,
        onDownloadStatusRequest,
        onFloorPlanSelect,
        floorplanHref,
        onScroll,
    } = properties;
    const [collectionState, setCollection] = useState<IHamburgerItemViewProperties[] | undefined>();
    const [sideMenuState, setSideMenu] = useState<IHamburgerSideMenuViewProperties | undefined>();
    const [navBarState, setNavBar] = useState<IHamburgerNavigationBarProperties | undefined>();
    const [activeItemIndex, setActiveItemIndex] = useExpandedItemIndex(
        `tour-index-${properties.topBar.title.text}`,
        injectOrBuildContainer(),
    );
    const activeItem = activeItemIndex !== null ? collectionState?.at(activeItemIndex) : null;

    const setExpandedIndex = (expandedIndex: number | null) => {
        setCollection(collectionIn => {
            if (!collectionIn) {
                return collectionIn;
            }
            /* We need to shallow copy the root, object, collection & items, otherwise the comparisons will match & fail to re-render */
            const collectionOut = collectionIn.map((collectionItem, index) =>
                _changeIsShowing(
                    collectionItem,
                    expandedIndex !== null ? expandedIndex === index : false,
                ),
            );
            return collectionOut;
        });
    };

    useEffect(() => {
        if (activeItem && activeItem.subItems.length > 0) {
            const data = activeItem.data as IHamburgerItemViewData;
            setExpandedIndex(data.index);
        }
    }, [activeItem]);

    const hideSideMenu = useCallback(() => {
        setSideMenu(stateIn =>
            stateIn
                ? {
                      ...stateIn,
                      isShowing: false,
                  }
                : undefined,
        );
    }, [setSideMenu]);

    const toggleSideMenuShowing = useCallback(() => {
        setSideMenu(stateIn =>
            stateIn
                ? {
                      ...stateIn,
                      isShowing: !stateIn.isShowing,
                  }
                : undefined,
        );
    }, [setSideMenu]);

    const onDownloadToggle = useCallback(
        (shouldDownload: boolean) => {
            hideSideMenu();
            onDownloadStatusRequest?.(shouldDownload);
        },
        [onDownloadStatusRequest, hideSideMenu],
    );

    const onListItemClick = useCallback(
        (item: IHamburgerItemViewProperties) => {
            const data = item.data as IHamburgerItemViewData;

            setActiveItemIndex(data.index);

            if (item.subItems.length === 0) {
                /* Launch item (ignore and allow nextjs to handle if there is a href) */
                data.model.onClick?.(data.model);
                setExpandedIndex(null);
            } else {
                /* Show sub items */
                setExpandedIndex(data.index);
            }
        },
        [setActiveItemIndex],
    );

    useEffect(() => {
        setCollection(
            collection.items.map((x, index) => mapItemToProperties(x, index, onListItemClick)),
        );
    }, [onListItemClick, collection]);

    useEffect(() => {
        setSideMenu({
            ...sideMenu,
            items: sideMenu.items.map(item => mapSideMenuItem(item)),
            isShowing: false,
            download: mapSideMenuDownload(sideMenu, hasContentDownloaded, onDownloadToggle),
            onOverlayClick: hideSideMenu,
        });
    }, [onDownloadToggle, hideSideMenu, hasContentDownloaded, sideMenu]);

    useEffect(() => {
        setNavBar(
            mapTopBar(
                topBar,
                showFloorPlanIcon,
                toggleSideMenuShowing /* OnHamburgerMenuClick */,
                onFloorPlanSelect,
                floorplanHref,
            ),
        );
    }, [onFloorPlanSelect, toggleSideMenuShowing, floorplanHref, showFloorPlanIcon, topBar]);

    return collectionState && sideMenuState && navBarState ? (
        <MainView
            backgroundColor={backgroundColor}
            backgroundImage={backgroundImage}
            collection={{
                items: collectionState,
                backgroundImage,
                backgroundColor,
                onScroll,
            }}
            sideMenu={sideMenuState}
            navBar={navBarState}
            onScroll={onScroll}
        />
    ) : null;
}

export default memo(View);
